<template>
    <div class="row">
        <div class="col-6">
            <jet-select
                v-model="stopCondition"
                :horizontal="false"
                :invalid-feedback="form.errors['recurrence.stopCondition']"
                :options="stopConditions"
                :required="true"
            />
        </div>
        <div v-if="modelValue.stopCondition === 'count'" class="col-6">
            <form-group :horizontal="false">
                <div class="flex items-center gap-2">
                    <input
                        v-model="count"
                        :aria-label="trans('general.component.repeat.ends.count_label')"
                        class="form-control !w-20"
                        min="1"
                        name="count"
                        type="number"
                        @blur="handleBlur"
                    />{{ trans('general.occurrences', { '%d': modelValue.count || 1 }) }}
                </div>
                <div v-if="form.errors['recurrence.count']" class="invalid-feedback" style="display: block">
                    {{ form.errors['recurrence.count'] }}
                </div>
            </form-group>
        </div>
        <div v-if="modelValue.stopCondition === 'until'" class="col-6">
            <jet-date-picker v-model="until" :horizontal="false" :invalid-feedback="form.errors['recurrence.until']" :min-date="minEndDate" />
        </div>
    </div>
</template>

<script setup>
import FormGroup from '@/Components/FormGroup.vue';
import JetDatePicker from '@/Jetstream/Datepicker.vue';
import JetSelect from '@/Jetstream/Select.vue';
import { trans } from '@/mixins/i18n';
import { DateTime } from 'luxon';
import { computed, watch } from 'vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    form: Object,
    modelValue: {
        type: Object,
        required: true,
    },
    minEndDate: Object,
});

const update = (changes) => emit('update:modelValue', { ...props.modelValue, ...changes });

// Computed get/set keep the recurrence object readonly from here: every write
// flows back through update:modelValue instead of mutating the shared prop.
// Changing the stop condition resets the dependent count/until so a stale value
// can't survive a switch.
const stopCondition = computed({
    get: () => props.modelValue.stopCondition,
    set: (value) => update({ stopCondition: value, count: 1, until: null }),
});

const count = computed({
    get: () => props.modelValue.count,
    set: (value) => update({ count: value }),
});

const until = computed({
    get: () => props.modelValue.until,
    set: (value) => update({ until: value }),
});

function toDateTime(value) {
    if (!value) {
        return null;
    }
    if (typeof value === 'string') {
        const parsed = DateTime.fromISO(value);
        return parsed.isValid ? parsed : null;
    }
    if (typeof value === 'object' && typeof value.toISODate === 'function') {
        return value;
    }
    return null;
}

watch(
    () => props.minEndDate,
    (newMinEndDate) => {
        if (!props.modelValue.until || !newMinEndDate) {
            return;
        }
        // Only clear `until` when the new start date pushes it out of range;
        // otherwise the user loses their recurrence end-date every time they
        // adjust the start. The date picker stores `until` as a yyyy-MM-dd
        // string while `minEndDate` is a luxon DateTime — normalise both
        // before comparing, otherwise the `>=` check coerces the string to
        // NaN and clears unconditionally.
        const currentUntil = toDateTime(props.modelValue.until);
        const start = toDateTime(newMinEndDate);
        if (!currentUntil || !start) {
            return;
        }
        if (currentUntil.startOf('day') >= start.startOf('day')) {
            return;
        }
        update({ until: null });
    },
);

const stopConditions = [
    { value: 'never', label: trans('general.component.repeat.ends.never') },
    { value: 'count', label: trans('general.component.repeat.ends.after_x_occurrences') },
    { value: 'until', label: trans('general.component.repeat.ends.on_specific_date') },
];

const handleBlur = (event) => {
    const parsed = Number(event.target.value);
    const clamped = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;

    event.target.value = String(clamped);
    update({ count: clamped });
};
</script>
